/**
 * Job submission (form and declarative spec), projects, and the job reads behind the
 * Jobs screen: list, detail, outputs, logs, preview and cancel.
 *
 * Part of the Console's API client: import from `engine/api.js`, which re-exports this module.
 */

import {
  BASE,
  headers,
  fetchJson,
  fetchNoContent,
  fetchJsonCoded,
} from "./apiTransport";

/**
 * Drop null / empty-string / empty-array fields from a payload so a bare submit
 * stays byte-compatible with the pre-F03 request (the backend treats absent
 * optional workload fields as their defaults). `false`, `0`, and populated
 * objects/arrays are preserved.
 */
function pruneEmpty(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined) continue;
    if (v === "") continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0) continue;
    out[k] = v;
  }
  return out;
}

/**
 * POST /jobs — submit a job through Adam/Eve via the HSA scheduler.
 *
 * Accepts the full F03 workload spec (E01 container_image/command/args/env/
 * secret_refs/gpu/timeout/retries + E02.2 external/manifest `inputs[]`).
 * Null/empty workload fields are pruned so a resource-only submit produces the
 * same byte payload as before F03. secret_refs carry REFERENCES only — never a
 * literal secret value (enforced upstream by jobValidation + useJobForm).
 *
 * E02.7 managed upload: when `spec.input_plan` is set (mode="managed_upload"
 * with a declared file manifest), this creates a `waiting_for_inputs` job + open
 * upload session in one atomic call. That path sends `io_mode:"managed_wrapper"`
 * and REQUIRES an `Idempotency-Key` header (passed as `opts.idempotencyKey`) so
 * a retried create returns the same job/session instead of a duplicate. A
 * synchronous (non-managed) submit sends neither payload field, keeping its body
 * byte-compatible with the pre-E02.7 request.
 *
 * OMEGA-867 — the SYNCHRONOUS submit now also honours `opts.idempotencyKey`, and
 * the caller should always pass one. The header is optional on that path server
 * side, but omitting it means a request the backend commits whose response is
 * lost turns into a SECOND billable job on retry, which no client-side guard can
 * prevent (the client cannot know whether the server committed). The key must be
 * STABLE across retries of one submission intent — see `useSubmitJob`, which owns
 * that lifetime; a key regenerated per attempt closes nothing.
 *
 * Both paths use the coded-error seam so a caller can branch on `err.code`
 * (`idempotency_conflict` in particular). It is a strict superset of the plain
 * seam — same readable `.message` — so the synchronous path loses nothing by it.
 */
export async function submitJob(spec = {}, opts = {}) {
  const {
    service_class,
    compute_required,
    memory_required,
    sovereign_region,
    esg_required,
    container_image,
    command,
    args,
    env,
    secret_refs,
    gpu_type,
    gpu_count,
    timeout_secs,
    max_retries,
    inputs,
    workload_name,
    project_id,
    input_plan,
  } = spec;

  const managed = input_plan && input_plan.mode === "managed_upload";

  const body = pruneEmpty({
    client_id: "omega-ai-hpc-console",
    service_class,
    compute_required,
    memory_required,
    bandwidth_required: 1.0,
    sovereign_region: sovereign_region || null,
    esg_required,
    container_image,
    command,
    args,
    env,
    secret_refs,
    // gpu_type defaults to "none" on the backend; only send a concrete value.
    gpu_type: gpu_type && gpu_type !== "none" ? gpu_type : null,
    gpu_count: gpu_count || null,
    timeout_secs,
    max_retries: max_retries || null,
    inputs,
    // Job identity (Submit UX + Job Identity PRD, FR3). Both fields are REQUIRED
    // since E14.2 / OMEGA-466 — the backend rejects a submission missing either
    // with 422 (jobs.project_id / jobs.workload_name are NOT NULL). pruneEmpty must
    // NOT drop them; the Submit UX enforces both are present before calling this.
    workload_name,
    project_id,
    // E02.7 — only present for a managed-upload create; io_mode must be
    // managed_wrapper for that path (the backend rejects other combinations).
    input_plan: managed ? input_plan : null,
    io_mode: managed ? "managed_wrapper" : null,
  });

  const h = headers();
  if (opts.idempotencyKey) h["Idempotency-Key"] = opts.idempotencyKey;

  // OMEGA-867 — BOTH paths surface the stable coded errors (409 idempotency_conflict,
  // 422 idempotency_key_too_long, 429 quota_exceeded, 503 ...) so the Console can name the
  // cause. The synchronous path used the plain seam, which was fine while it sent no key and
  // so could never receive an idempotency verdict; now that it can, a caller that cannot read
  // `err.code` would have to pattern-match a prose sentence to tell a conflict from a quota
  // rejection. `fetchJsonCoded` is a strict superset (identical readable `.message`, plus the
  // coded fields), so nothing regresses for existing callers.
  return fetchJsonCoded(`${BASE}/jobs`, {
    method: "POST",
    headers: h,
    body: JSON.stringify(body),
  });
}

/**
 * POST /jobs/spec:validate — dry-run validate a declarative YAML job spec
 * (Submit UX PRD FR5). Body is { format, spec }; `format` defaults to "omega"
 * (native) when falsy, else "k8s". Detection only: this writes ZERO rows and
 * dispatches nothing.
 *
 * CRITICAL: this endpoint returns HTTP 200 even when the spec is INVALID — the
 * reasons ride in the body as { valid: false, submission: null, errors: [...] }.
 * So we do NOT throw on `valid: false`; we return the parsed
 * JobSpecPreviewResponse and let the caller branch on `body.valid`. We only
 * throw on a non-2xx (e.g. 503 "Database not available", which a dry-run must
 * surface honestly rather than mask as "invalid spec").
 */
export async function validateJobSpec(format, spec) {
  return fetchJson(`${BASE}/jobs/spec:validate`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ format: format || "omega", spec }),
  });
}

/**
 * POST /jobs/spec — submit a job from a declarative YAML spec (Submit UX PRD
 * FR6). Body is { format, spec }; `format` defaults to "omega" when falsy.
 * Returns the SAME JobResponse shape as submitJob. On a non-2xx (422 for a
 * bad/unmappable spec, an unknown/archived project, or a failed submission
 * constraint) the NEW error envelope's human `message` is thrown via
 * readDetail(err), rendering as a readable sentence, never "[object Object]".
 *
 * OMEGA-867 — takes the same optional `idempotencyKey` as submitJob, because this
 * endpoint delegates to the SAME create and was equally exposed to a lost response
 * becoming a second billable job. The server fingerprints the MAPPED submission
 * rather than the raw YAML, so two byte-different specs that map to the same job
 * replay each other instead of conflicting. Coded-error seam for the same reason
 * as submitJob: a caller must be able to tell a conflict from a bad spec.
 */
export async function submitJobSpec(format, spec, idempotencyKey) {
  const h = headers();
  if (idempotencyKey) h["Idempotency-Key"] = idempotencyKey;
  return fetchJsonCoded(`${BASE}/jobs/spec`, {
    method: "POST",
    headers: h,
    body: JSON.stringify({ format: format || "omega", spec }),
  });
}

/**
 * GET /projects — the authenticated tenant's NON-archived projects, newest first
 * (Submit UX + Job Identity PRD, FR1). Returns { projects: [{ project_id,
 * tenant_id, name, created_at, archived_at }] }. Server-ordered — DO NOT re-sort.
 * The owning tenant is resolved from the JWT, never a query param. Errors: 400
 * without a tenant claim (object detail; readDetail surfaces its message), 503
 * degraded, 401 on a bad token.
 */
export async function listProjects() {
  return fetchJson(`${BASE}/projects`, { headers: headers() });
}

/**
 * POST /projects — create a tenant-scoped project (FR1). Body is ONLY { name };
 * the owning tenant is taken from the JWT, never the body. Returns 201 with the
 * created project. Errors: 409 "a project named '<name>' already exists for this
 * tenant" (object detail; readDetail surfaces its message — names are unique per
 * tenant), 422 (name 1..128), 400 without a tenant claim, 503 degraded.
 */
export async function createProject(name) {
  return fetchJson(`${BASE}/projects`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ name }),
  });
}

/**
 * DELETE /projects/{id} — soft-delete (archive) a project (FR2). Returns 204 with
 * NO body, so we resolve on res.ok WITHOUT calling res.json(). Errors: 404 unknown
 * OR cross-tenant (never confirms another tenant's project exists), 409
 * "project has active (non-terminal) jobs and cannot be deleted" (object detail;
 * readDetail surfaces its message — the UI renders it readably), 400/503.
 */
export async function deleteProject(projectId) {
  // 204 No Content — there is no body to parse.
  return fetchNoContent(`${BASE}/projects/${encodeURIComponent(projectId)}`, {
    method: "DELETE",
    headers: headers(),
  });
}

/** GET /jobs - list recent jobs from the database. Returns { jobs, note? }. */
/**
 * GET /jobs — the tenant's job list.
 *
 * Two mutually independent filters, both optional:
 * - `status` is an EXACT match on `jobs.status` (the long-standing contract).
 * - `statusGroup` (OMEGA-829) is the coarse `pending` | `completed` split the Jobs
 *   screen's three-pill bar sends. `completed` covers every TERMINAL status, so a
 *   failed / vetoed / rejected / cancelled job is still reachable from a named filter;
 *   `pending` is its complement. Any other value is a 422 from the backend, never a
 *   silently unfiltered list.
 */
export async function listJobs(limit = 20, status, statusGroup) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (status) qs.set("status", status);
  if (statusGroup) qs.set("status_group", statusGroup);
  return fetchJson(`${BASE}/jobs?${qs}`, { headers: headers() });
}

/**
 * GET /jobs/{id} - job detail with lifecycle, routing evidence (Adam/Eve
 * reasoning, profit, utility, rounds) and the latest execution outcome.
 */
export async function getJob(jobId) {
  return fetchJson(`${BASE}/jobs/${encodeURIComponent(jobId)}`, { headers: headers() });
}

/**
 * GET /jobs/{id}/outputs?limit=N — list a job's collected output artifacts
 * (E02.6 FR1). Returns { job_id, status, outputs[] } where each output is
 * { artifact_id, filename, uri, size_bytes?, checksum_sha256?, content_type?,
 * download_url?, expiry_secs? }. Server returns them in deterministic order
 * (uri ASC, artifact_id ASC) — consumers MUST NOT re-sort. A non-terminal job
 * returns 200 with an empty list so the panel can poll.
 */
export async function getJobOutputs(jobId, limit = 50) {
  const qs = new URLSearchParams({ limit: String(limit) });
  return fetchJson(`${BASE}/jobs/${encodeURIComponent(jobId)}/outputs?${qs}`, { headers: headers() });
}

/**
 * GET /jobs/{id}/logs — resolve a job's log reference (E02.6 FR2). Returns
 * { job_id, status, logs_url?, source?: "job"|"latest_outcome", note? }. When
 * no URL exists the backend supplies a human-readable `note`.
 */
export async function getJobLogs(jobId) {
  return fetchJson(`${BASE}/jobs/${encodeURIComponent(jobId)}/logs`, { headers: headers() });
}

/**
 * GET /jobs/{id}/log-events?cursor=&direction=&limit= — one page of a job's
 * structured log events, read live from the provider (E02.9). Returns
 * `{ job_id, status, events[{timestamp, ingestion_time?, message, stream,
 * event_id?}], cursors: {older?, newer?}, availability, partial, truncated }`.
 * `cursor` is opaque and application-owned — never a raw CloudWatch token; pass
 * it straight through from a previous response's `cursors.older`/`cursors.newer`.
 * `direction` defaults to "older" (null cursor + no direction = the newest
 * tail page). Throws on a non-2xx (429 upstream_throttled / 502
 * upstream_unavailable / 400 cursor_expired) — the other availability states
 * (`not_dispatched` / `stream_pending` / `available` / `unsupported_provider` /
 * `multi_run_unsupported` / `expired_or_missing`) are always 200 and carried in
 * the body's `availability` field.
 */
export async function getJobLogEvents(jobId, { cursor, direction, limit } = {}) {
  const qs = new URLSearchParams();
  if (cursor) qs.set("cursor", cursor);
  if (direction) qs.set("direction", direction);
  if (limit != null) qs.set("limit", String(limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return fetchJson(`${BASE}/jobs/${encodeURIComponent(jobId)}/log-events${suffix}`, { headers: headers() });
}

/**
 * Fetch the bytes of a presigned download URL for in-browser preview (F05 FR4).
 *
 * Like postToObjectStore, this is the documented exception to the "all
 * network via api.js helpers carry the Bearer token" rule: a presigned URL
 * carries its own signature in the query string, so attaching an Authorization
 * header would break the object-store signature. Returns the response body as
 * text; throws the real HTTP status on a non-2xx so the caller can surface it
 * (never fabricated content).
 */
export async function fetchPreview(downloadUrl) {
  // Intentionally NO Authorization header — the presigned URL is the auth.
  const res = await fetch(downloadUrl);
  if (!res.ok) {
    throw new Error(`Preview failed: HTTP ${res.status}`);
  }
  return res.text();
}

/**
 * POST /jobs/{id}:cancel — request cancellation of a non-terminal job (E04.6).
 *
 * The `:cancel` verb sits outside the encoded job id (matching
 * inputs:presign / inputs:confirm). Returns the 200 receipt
 * ({ job_id, status, cancelled, provider_run_id?, message }) on success. A 409
 * arrives on the NEW error envelope; readDetail surfaces its human `message`,
 * so the thrown Error message is the readable string.
 */
export async function cancelJob(jobId) {
  return fetchJson(`${BASE}/jobs/${encodeURIComponent(jobId)}:cancel`, {
    method: "POST",
    headers: headers(),
  });
}
